import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { ILocation, ISharedEntity, ISharedTask, LocaleStringMap, TaskApiService } from 'shared';

import { LazyTranslateLoader } from '../../utils/lazy-translate-loader';
import { IGeneratedUrl } from '../models';
import { AppSettingsService } from './app-settings.service';
import { MapService } from './map.service';

interface IEntity {
    id: string;
    name?: string | LocaleStringMap;
}

@Injectable({ providedIn: 'root' })
export class SharingService {
    private mapService = inject(MapService);
    private taskApiService = inject(TaskApiService);
    private appSettingsService = inject(AppSettingsService);
    private translate = inject(TranslateService);

    private readonly _sharedEntity: BehaviorSubject<ISharedEntity> = new BehaviorSubject<ISharedEntity>(null);
    public readonly sharedEntity: Observable<ISharedEntity> = this._sharedEntity.asObservable();

    // Public API

    public async generateShareUrl(
        itemId: string,
        itemType: 'TASK' | 'ISSUE',
        expiresAt: number,
        allowFinish: boolean
    ): Promise<IGeneratedUrl> {
        const settings = await firstValueFrom(this.appSettingsService.appSettings);
        const result = await firstValueFrom(
            this.taskApiService.generateShareUrl(itemId, itemType, expiresAt, allowFinish)
        );
        return {
            shareId: result.id,
            shareUrl: `${settings.url.app}${settings.url.app.endsWith('/') ? '' : '/'}shared/${result.tenantId}/${
                result.id
            }`,
        };
    }

    public async updateShareUrl(shareId: string, expiresAt: number, allowFinish: boolean): Promise<IGeneratedUrl> {
        const settings = await firstValueFrom(this.appSettingsService.appSettings);
        const result = await firstValueFrom(this.taskApiService.updateShareUrl(shareId, expiresAt, allowFinish));
        return {
            shareId: result.id,
            shareUrl: `${settings.url.app}${settings.url.app.endsWith('/') ? '' : '/'}shared/${result.id}`,
        };
    }

    public async fetchSharedEntity(shareId: string, tenantId: string): Promise<ISharedEntity> {
        const entity = await firstValueFrom(this.taskApiService.getSharedEntity(shareId, tenantId));
        await this.mapService.fetchMapConfig(tenantId);
        await this.updateTranslations(entity);
        this.addNamedEntitySubType(entity);
        this._sharedEntity.next(entity);
        return entity;
    }

    public async disposeSharedEntity(): Promise<void> {
        this._sharedEntity.next(null);
    }

    public getLocationForEntity(entity: ISharedEntity): ILocation {
        switch (entity.type) {
            case 'SHARED_TASK':
                return entity.task.location;
            case 'SHARED_ISSUE':
                return entity.issue.location;
        }
    }

    public updateSharedEntity(entity: ISharedEntity): void {
        if (this._sharedEntity.value.id === entity.id) this._sharedEntity.next(entity);
    }

    public async finishSharedTask(): Promise<ISharedTask> {
        if (!this._sharedEntity.value || this._sharedEntity.value.type !== 'SHARED_TASK') return;
        const entityId = this._sharedEntity.value.id;
        const tenantId = this._sharedEntity.value.tenantId;
        // Store previous status in case of revert
        const previousStatus = (this._sharedEntity.value as ISharedTask).task.status;
        // Set 'DONE' status on current entity
        this._sharedEntity.next(
            Object.assign(this._sharedEntity.value, {
                task: Object.assign(this._sharedEntity.value.task, { status: 'DONE' }),
            })
        );
        // Finish
        let entity;
        try {
            entity = (await firstValueFrom(
                this.taskApiService.shareLinkAction(entityId, 'FinishTask', tenantId)
            )) as ISharedTask;
        } catch (e) {
            // Restore previous status on error
            this._sharedEntity.next(
                Object.assign(this._sharedEntity.value, {
                    task: Object.assign(this._sharedEntity.value.task, { status: previousStatus }),
                })
            );
            throw e;
        }
        await this.updateTranslations(entity);
        this.addNamedEntitySubType(entity);
        this._sharedEntity.next(entity);
        return entity;
    }

    // Private functions
    private setEntityName(entity: IEntity, prefix: string): void {
        if (entity) {
            entity.name = `${prefix}.${entity.id}`;
        }
    }

    private updateTranslationFor(entity: IEntity, translationKey: string): void {
        if (entity && typeof entity.name === 'object') {
            Object.entries(entity.name).forEach(([lang, name]) =>
                this.translate.setTranslation(lang, { [translationKey]: { [entity.id]: name } }, true)
            );
        }
    }

    private addNamedEntitySubType(entity: ISharedEntity): void {
        if (entity.subjectType) {
            this.setEntityName(entity.subjectType, 'subjectType');
        }
        switch (entity.type) {
            case 'SHARED_ISSUE':
                this.setEntityName(entity.issueType, 'issueType');
                break;
            case 'SHARED_TASK':
                this.setEntityName(entity.taskType, 'taskType');
                if (entity.linkedIssue) {
                    if (entity.linkedIssueType) {
                        this.setEntityName(entity.linkedIssueType, 'issueType');
                    }
                    if (entity.linkedIssueSubjectType) {
                        this.setEntityName(entity.linkedIssueSubjectType, 'subjectType');
                    }
                }
                break;
        }
    }

    private async updateTranslations(entity: ISharedEntity): Promise<void> {
        // Wait for the base translations to load.
        await firstValueFrom((this.translate.currentLoader as LazyTranslateLoader).translationsLoaded$);

        if (entity.subjectType) {
            this.updateTranslationFor(entity.subjectType, 'subjectType');
        }
        switch (entity.type) {
            case 'SHARED_ISSUE':
                this.updateTranslationFor(entity.issueType, 'issueType');
                break;
            case 'SHARED_TASK':
                this.updateTranslationFor(entity.taskType, 'taskType');
                if (entity.linkedIssueType) {
                    this.updateTranslationFor(entity.linkedIssueType, 'issueType');
                }
                break;
        }
    }
}
